import Button from '@rio-cloud/rio-uikit/Button';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { ConnectedOnboardingTip } from '../../common/onboarding/ConnectedOnboardingTip';
import { Tips } from '../../common/onboarding/types';
import { SUBSCRIPTION_WRITE } from '../../common/permissions/permissions';
import { getPermissions } from '../../common/permissions/redux/permissions.redux';
import { isProductLegacy } from '../../common/product/productService';
import {
    getContracts,
    getProductForUpdate,
    termedLevelRenewalActions,
} from '../../common/termedLevel/redux/termedLevelRenewal.redux';
import type { TermedLevelId } from '../../common/termedLevel/redux/types';
import { OverlayTrigger, Tooltip } from '../../common/utils/tooltipReExports';
import { DEFAULT_TOOLTIP_DELAY } from '../../config/config';
import { getConfigureRenewalAutoTrackingKey } from './contractTracking';
import { getSelectedContracts } from './redux/contract.redux';

interface Props {
    productId: string;
    serviceName: string;
    updateContract: () => void;
    areContractsSelected: boolean;
    hasPermission: boolean;
    isProductLegacy: boolean;
}

export const RenewButton = ({
    updateContract,
    areContractsSelected,
    hasPermission,
    isProductLegacy,
    serviceName,
}: Props) => {
    const isButtonDisabled = !areContractsSelected || !hasPermission || isProductLegacy;

    const button = (
        <Button
            className='btn btn-primary'
            onClick={updateContract}
            disabled={isButtonDisabled}
            style={isButtonDisabled ? { pointerEvents: 'none' } : undefined}
            type={'button'}
            autoTrackingKey={getConfigureRenewalAutoTrackingKey(serviceName)}
            data-onboarding-contracts-walkthrough-step='contract-view-renew-bulk-button'
        >
            <span className='rioglyph rioglyph-pencil' aria-hidden={true} />
            <FormattedMessage id='intl-msg:common-message.contracts.configureRenewal' />
        </Button>
    );

    if (isProductLegacy) {
        return (
            <OverlayTrigger
                trigger={OverlayTrigger.TRIGGER_HOVER}
                placement='top'
                overlay={
                    <Tooltip id='legacy-tooltip'>
                        <FormattedMessage id='marketplace.product.legacy.tooltip' />
                    </Tooltip>
                }
                delay={DEFAULT_TOOLTIP_DELAY}
            >
                <span className='d-inline-block'>{button}</span>
            </OverlayTrigger>
        );
    }

    if (!areContractsSelected || !hasPermission) {
        return button;
    }

    return (
        <ConnectedOnboardingTip
            tipType={Tips.RENEW_CONTRACTS}
            contentTranslationId={'marketplace.contracts.renewal.dialog.text.plural'}
            placement={'top'}
        >
            {button}
        </ConnectedOnboardingTip>
    );
};

const mapStateToProps = (state: RootState, ownProps: Pick<Props, 'productId'>) => {
    const selectedContracts = getSelectedContracts(state).filter(contract => contract.productId === ownProps.productId);

    const product = getProductForUpdate(state);

    const isLegacyProduct = product?.productId === ownProps.productId && isProductLegacy(product);

    return {
        areContractsSelected: selectedContracts.length > 0,
        hasPermission: getPermissions(state)?.includes(SUBSCRIPTION_WRITE) ?? false,
        isProductLegacy: isLegacyProduct,
    };
};

const mapDispatchToProps = (dispatch: RootDispatch, ownProps: Pick<Props, 'productId'>) => ({
    updateContract: () => {
        dispatch(updateContractThunk(ownProps.productId));
    },
});

function getHighestRenewalLevel(state: RootState, productId: string, selectedContracts: TermedLevelId[]) {
    const contracts = getContracts(state);
    const assetIds = selectedContracts.map(it => it.resourceId);

    const filteredContracts = contracts.filter(
        contract => contract.productId === productId && assetIds.includes(contract.resourceId)
    );

    const level = filteredContracts
        .flatMap(contract => (contract.renewsToLevel ? [contract.renewsToLevel] : []))
        .reduce((a, b) => (a > b ? a : b), '');

    if (level === '') {
        return;
    }
    return level;
}

function updateContractThunk(productId: string) {
    return (dispatch: RootDispatch, getState: () => RootState) => {
        const selectedContracts = getSelectedContracts(getState()).filter(contract => contract.productId === productId);
        const highestLevel = getHighestRenewalLevel(getState(), productId, selectedContracts);

        dispatch(
            termedLevelRenewalActions.selectForUpdate({
                renewsToLevel: highestLevel,
                contracts: selectedContracts,
                productId,
            })
        );
    };
}

export const RenewButtonContainer = connect(mapStateToProps, mapDispatchToProps)(RenewButton);
